import { DividerProps } from "rc-menu/lib/Divider";
import React, { useState } from "react";
import { getArtists } from "../../../actions/user";
import "../../../assets/scss/components/track/performer.scss";
import { Select, Checkbox, AutoComplete, Tooltip } from "antd";
import { filterOption, getSelectOptions } from "../../../utils";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { CloseCircleOutlined, LoadingOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Performer } from "@type/global.types";
import { Backend, Frontend } from "shared-types";

interface PerformerProps extends DividerProps {
    performers: Performer[];
    roles: Backend.T_MemberRole[];
    onChange: (performers: Performer[]) => any;
    disabled?: boolean;
}

const PerformerWidget: React.FC<PerformerProps> = (props): React.ReactElement => {
    const {
        performers,
        roles,
        onChange,
        disabled,
        ...rest
    } = props;

    const [artists, setArtists] = useState<Frontend.T_SelectItem[]>([]);
    const [artistsSearchTimer, setArtistsSearchTimer] = useState<NodeJS.Timeout>();
    const [isArtistLoading, setArtistLoading] = useState(false);

    const onChangeRole = (index: number) => {
        return (value: number) => {
            performers[index].role = value;
            onChange([...performers]);
        }
    }

    const onChangePrimary = (index: number) => {
        return (e: CheckboxChangeEvent) => {
            performers[index].primary = e.target.checked;
            onChange([...performers]);
        }
    }

    const onDeletePerformer = (index: number) => {
        return () => {
            performers.splice(index, 1);
            onChange([...performers]);
        }
    }

    const onClickAddPerformer = () => {
        performers.push({ id: -1, primary: true, role: -1, title: '' });
        onChange([...performers]);
    }

    const onSearchArtists = (e: string) => {
        clearTimeout(artistsSearchTimer);
        setArtistsSearchTimer(setTimeout(() => handleArtistSearch(e), 500));
    }

    const onChangeArtist = (index: number) => {
        return (value: string) => {
            performers[index].title = value;
            onChange([...performers]);
        }
    }

    const onSelectArtist = (index: number) => {
        return (_value: string, option: Frontend.T_SelectItem) => {
            performers[index].title = option.label;
            performers[index].id = option.value;
            onChange([...performers]);
        }
    }

    const handleArtistSearch = async (value: string) => {
        setArtistLoading(true);
        try {
            let artists = await getArtists(value);
            const options: Frontend.T_SelectItem[] = artists.result.list?.map((item: any) => ({
                value: item.id,
                label: item.name
            }));
            setArtists([...options]);
        } catch (error) {

        }
        setArtistLoading(false);
    }


    return (
        <div {...rest} className={`${rest.className} performer-widget${disabled ? ` disabled` : ``}`}>
            <table className="performer-table">
                <thead>
                    <tr>
                        <th className="tw-w-[40%]">Performer</th>
                        <th className="tw-w-[40%]">Role</th>
                        <th className="tw-w-[25px]">Primary</th>
                        <th className="tw-w-[25px]">
                            <Tooltip title="Add new performer">
                                <PlusCircleOutlined rev={undefined} onClick={onClickAddPerformer} className="tw-mb-2" disabled={disabled} />
                            </Tooltip>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {
                        performers?.map((performer: Performer, index: number) => {
                            return (
                                <tr key={index}>
                                    <td>
                                        <AutoComplete
                                            onSearch={onSearchArtists}
                                            onSelect={onSelectArtist(index)}
                                            onChange={onChangeArtist(index)}
                                            options={isArtistLoading ? [] : artists}
                                            value={performer.title}
                                            size="middle"
                                            placeholder="Search keyword"
                                            showSearch
                                            notFoundContent={isArtistLoading ? <LoadingOutlined rev={undefined} /> : ""}
                                            disabled={disabled}
                                        />
                                    </td>
                                    <td>
                                        <Select
                                            options={getSelectOptions(roles)}
                                            placeholder="Please select Role"
                                            onChange={onChangeRole(index)}
                                            value={(performer.role > -1 && roles.length) ? performer.role : undefined}
                                            size="middle"
                                            showSearch
                                            filterOption={filterOption}
                                            disabled={disabled}
                                        />
                                    </td>
                                    <td className="text-center">
                                        <Checkbox checked={performer.primary === true} onChange={onChangePrimary(index)} disabled={disabled} />
                                    </td>
                                    <td>
                                        <CloseCircleOutlined rev={undefined} onClick={onDeletePerformer(index)} disabled={disabled} />
                                    </td>
                                </tr>
                            )
                        })
                    }
                </tbody>
            </table>
        </div>
    )
}

export default PerformerWidget;
export type { Performer, PerformerProps };
